<template>
  <div>
    <loading-panel :loading="loading"></loading-panel>

    <!-- Header -->
    <div v-if="!add_template && !edit_template">
      <filter-bar
        :filters="groupFilterSchema"
        :initial-value="filterValues"
        :result-count="totalRows"
        :per-page="perPage"
        :per-page-options="[5, 10, 25, 50]"
        saved-filters-key="promo-code-groups"
        mobile-collapsible
        :sort-options="groupSortOptions"
        :sort-value="sort"
        :show-sort-chip="isMobile"
        @change="onFiltersChanged"
        @update:perPage="onPerPageChange"
        @update:sort="onSortChange"
      ></filter-bar>

      <data-table
        class="mt-3"
        :columns="groupColumns"
        :rows="templates"
        row-key="id"
        :loading="loading"
        :page="currentPage"
        :per-page="perPage"
        :total="totalRows"
        :sort-value="sort"
        @update:sort="onSortChange"
        @update:page="onPageChange"
      >
        <template #cell(name)="{ row }">
          <div>{{row.name}}</div>
          <badge type="info" v-if="row.has_template">has default settings</badge>
        </template>
        <template #cell(description)="{ row }">
          <small>{{row.description || '-'}}</small>
        </template>
        <template #actions="{ row }">
          <el-dropdown>
            <base-button link type="primary" size="sm">
              <i style="font-size: large" class="tim-icons el-icon-more"></i>
            </base-button>
            <template #dropdown>
              <el-dropdown-menu>
                <a class="dropdown-item mt-1 mb-2" href="#"
                   v-on:click="editTemplate(row, $event)">
                  <i class="tim-icons text-default icon-pencil mr-2"></i> Edit promo code group
                </a>
                <a class="dropdown-item mt-1 mb-2" href="#"
                   v-on:click="addPromoToGroup(row, $event)">
                  <i class="tim-icons text-default icon-simple-add mr-2"></i> Add promo codes to group
                </a>
                <a class="dropdown-item mt-1 mb-2" href="#"
                   :class="{ 'dropdown-item-na': !row.has_template }"
                   :title="row.has_template ? null : 'This group has no default settings. Configure them first.'"
                   v-on:click="row.has_template ? applySettingsToAllPromoCodesPrompt(row, $event) : $event.preventDefault()">
                  <i class="tim-icons text-default icon-refresh-02 mr-2"></i> Apply settings to all promo codes in group
                </a>
                <a class="dropdown-item mt-1 mb-2" href="#"
                   :class="{ 'dropdown-item-na': !row.has_template }"
                   :title="row.has_template ? null : 'This group has no default settings. Configure them first.'"
                   v-on:click="row.has_template ? viewGroupPromos(row, $event) : $event.preventDefault()">
                  <i class="tim-icons text-default icon-bullet-list-67 mr-2"></i> Apply settings to a single promo code
                </a>
                <hr>
                <a class="dropdown-item mt-1 mb-2" href="#"
                   v-on:click="deleteTemplatePrompt(row, $event)">
                  <i class="tim-icons text-default icon-trash-simple mr-2"></i> Delete promo code group
                </a>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </template>
        <template #empty>
          <p class="data-table__empty-text">No promo code groups found.</p>
        </template>
      </data-table>
    </div>

    <!-- Promo Code Group Form (Create/Edit) -->
    <div v-if="add_template || edit_template" :key="un_key">
      <form-section title="Promo code group details">
      <div class="row">
        <div class="col-lg-6">
          <div class="row">
            <div class="col">
              <label class="text-white">Group name *</label>
              <base-input required
                          :error="errs.name"
                          v-model="template.name"
                          placeholder="Group name">
              </base-input>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <base-input label="Description">
                <el-input style="background-color: inherit"
                          v-model="template.description"
                          :rows="5"
                          type="textarea"
                          placeholder="Group description"
                />
              </base-input>
            </div>
          </div>
        </div>
      </div>

      <div class="row mt-3" v-if="!show_template_details && (add_template || edit_template)">
        <div class="col-lg-6">
          <base-button style="padding: 0px" link type="primary"
                       v-on:click="show_template_details = true" size="sm">
            <i class="tim-icons icon-simple-add mr-1"></i> Configure default settings
          </base-button>
          <info-hint variant="neutral" class="mt-2 mb-0">
            Configure default settings to create a reusable configuration that lets you quickly apply predefined settings to promo codes in this group. A campaign can only issue codes from a group that has them.
          </info-hint>
        </div>
      </div>
      </form-section>

      <div v-if="show_template_details">
      <form-section title="Default settings">
        <template #actions>
          <base-button style="padding: 0px" v-on:click="show_template_details = false" link type="primary" size="sm">
            <i class="tim-icons icon-simple-remove mr-1"></i> Remove default settings
          </base-button>
        </template>

        <div class="row">
          <div class="col">
            <div class="row">
              <div class="col-lg-3">
                <base-input label="Redemption type">
                <el-select
                  v-model="template.redemption_type"
                  class="select-primary"
                  name="redemption_type"
                  placeholder="Redemption type"
                  value-key="id"
                >
                  <el-option
                    v-for="(type, index) in redemptionTypes"
                    class="select-primary"
                    :label="type.name"
                    :value="{name: type.name, id: type.id}"
                    :key="index + 'R'"
                  ></el-option>
                </el-select>
              </base-input>
            </div>
            <div class="col-lg-3 col-8">
              <label>Value type</label>
              <base-input>
                <el-select
                  v-model="template.value_type"
                  class="select-primary"
                  name="value_type"
                  placeholder="Value type"
                  value-key="id"
                >
                  <el-option
                    v-for="(type, index) in valueTypes"
                    class="select-primary"
                    :label="type.id === 1 ? type.name + ' (Recommended)' : type.name"
                    :value="{name: type.name, id: type.id}"
                    :key="index + 'VT'"
                  >
                    <span>{{ type.name }}</span>
                    <span v-if="type.id === 1" class="recommended-badge">Recommended</span>
                  </el-option>
                </el-select>
              </base-input>
            </div>
            <div class="col-lg-2">
              <base-input :error="errs.value_amount" label="Amount" type="number" placeholder="0.00">
                <el-input-number :controls="false" v-model="template.value_amount" :min="0" :precision="2"/>
              </base-input>
            </div>
          </div>
        </div>
      </div>

      </form-section>

      <form-section title="Purchase rules">

      <div class="row">
        <div class="col">
          <div class="row">
            <div class="col-lg-3">
              <base-input label="Allowed purchase dates">
                <el-select
                  v-model="template.purchase_all_dates"
                  class="select-primary"
                  name="value_type"
                  placeholder="Value type"
                  value-key="id"
                  v-on:change="un_key++"
                >
                  <el-option
                    v-for="(opt, index) in dateOptions"
                    class="select-primary"
                    :label="opt.name"
                    :value="{id: opt.id, name: opt.name}"
                    :key="index + 'DY'"
                  ></el-option>
                </el-select>
              </base-input>
            </div>

            <div class="col-lg-3 col-6" v-if="template.purchase_all_dates.id == 1">
              <base-input label="Purchase start date" :error="errs.purchase_start_date">
                <el-date-picker
                  type="date"
                  v-on:change="validateDateRange()"
                  placeholder="mm-dd-yyyy"
                  value-format="yyyy-MM-dd"
                  name="date"
                  v-model="template.purchase_start_date"
                  :format="date_format_cal"
                >
                </el-date-picker>
              </base-input>
            </div>
            <div class="col-lg-3 col-6" v-if="template.purchase_all_dates.id == 1">
              <base-input label="Purchase end date" :error="errs.purchase_end_date">
                <el-date-picker
                  type="date"
                  v-on:change="validateDateRange()"
                  placeholder="mm-dd-yyyy"
                  value-format="yyyy-MM-dd"
                  name="date"
                  v-model="template.purchase_end_date"
                  :format="date_format_cal"
                >
                </el-date-picker>
              </base-input>
            </div>
          </div>
        </div>
      </div>

      </form-section>

      <form-section title="Usage and application rules">

      <div class="row">
        <div class="col">
          <el-switch active-color="#e14eca" v-model="template.is_combineable" />
          <label> &nbsp; Can be combined with other promo codes</label>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <el-switch active-color="#e14eca" v-model="template.internal_only" />
          <label> &nbsp; Can only be applied internally</label>
        </div>
      </div>

      <div class="row mt-2" :key="un_key">
        <div class="col">
          <div class="row">
            <div class="col-lg-6">
              <base-input label="Applicable to">
                <el-select multiple clearable
                           v-model="template.purchasable_items"
                           class="select-primary"
                           name="value_type"
                           placeholder="Items"
                           value-key="uid"
                           v-on:change="un_key++"
                >
                  <el-option
                    v-for="(item, index) in purchasable_items"
                    class="select-primary"
                    :label="item.name"
                    :value="{id: item.id, name: item.name, item_type: item.item_type, uid: item.uid}"
                    :key="index + 'G'"
                  ></el-option>
                </el-select>
              </base-input>
            </div>
          </div>
          <base-button style="padding: 0px 0px 0px 0px" link type="primary" v-on:click="addAllPurchaseTypes(purchasable_items, 'game')">
            <i class="tim-icons icon-simple-add mr-1"></i> Add all games
          </base-button>
          <base-button class="ml-3" style="padding: 0px 0px 0px 0px" link type="primary" v-on:click="addAllPurchaseTypes(purchasable_items, 'item')">
            <i class="tim-icons icon-simple-add mr-1"></i> Add all items
          </base-button>
          <base-button class="ml-3" style="padding: 0px 0px 0px 0px" link type="primary" v-on:click="addAllPurchaseTypes(purchasable_items, 'gift_card_type')">
            <i class="tim-icons icon-simple-add mr-1"></i> Add all gift card types
          </base-button>
        </div>
      </div>

      <div class="row mt-3" v-if="appliesToGames()">
        <div class="col">
          <div class="row">
            <div class="col-lg-3">
              <base-input label="Allowed booking dates">
                <el-select
                  v-model="template.event_all_dates"
                  class="select-primary"
                  name="value_type"
                  placeholder="Booking date"
                  value-key="id"
                  v-on:change="un_key++"
                >
                  <el-option
                    v-for="(opt, index) in dateOptions"
                    class="select-primary"
                    :label="opt.name"
                    :value="{id: opt.id, name: opt.name}"
                    :key="index + 'DX'"
                  ></el-option>
                </el-select>
              </base-input>
            </div>

            <div class="col-lg-3 col-6" v-if="template.event_all_dates.id == 1">
              <base-input label="Event start date" :error="errs.event_start_date">
                <el-date-picker
                  type="date"
                  v-on:change="validateDateRange()"
                  placeholder="mm-dd-yyyy"
                  value-format="yyyy-MM-dd"
                  name="date"
                  v-model="template.event_start_date"
                  :format="date_format_cal"
                >
                </el-date-picker>
              </base-input>
            </div>
            <div class="col-lg-3 col-6" v-if="template.event_all_dates.id == 1">
              <base-input label="Event end date" :error="errs.event_end_date">
                <el-date-picker
                  type="date"
                  v-on:change="validateDateRange()"
                  placeholder="mm-dd-yyyy"
                  value-format="yyyy-MM-dd"
                  name="date"
                  v-model="template.event_end_date"
                  :format="date_format_cal"
                >
                </el-date-picker>
              </base-input>
            </div>
          </div>
          <div class="row">
            <div class="col-lg-3">
              <base-input label="Allowed booking time">
                <el-select
                  v-model="template.event_all_times"
                  class="select-primary"
                  name="value_type"
                  placeholder="Time option"
                  value-key="id"
                  v-on:change="un_key++"
                >
                  <el-option
                    v-for="(opt, index) in timeOptions"
                    class="select-primary"
                    :label="opt.name"
                    :value="{id: opt.id, name: opt.name}"
                    :key="index + 'XT'"
                  ></el-option>
                </el-select>
              </base-input>
            </div>
            <div class="col-lg-3 col-6" v-if="template.event_all_times.id == 1">
              <base-input label="Event start time" :error="errs.event_start_time">
                <el-time-picker v-on:change="validateDateRange()"
                                v-model="template.event_start_time"
                                :format="picker_time_format" placeholder="HH:MM"
                ></el-time-picker>
              </base-input>
            </div>
            <div class="col-lg-3 col-6" v-if="template.event_all_times.id == 1">
              <base-input label="Event end time" :error="errs.event_end_time">
                <el-time-picker v-on:change="validateDateRange()"
                                v-model="template.event_end_time"
                                :format="picker_time_format" placeholder="HH:MM"
                ></el-time-picker>
              </base-input>
            </div>
          </div>
          <label>Allowed days of the week</label>
          <div class="row">
            <div class="col">
              <base-checkbox v-model="template.available_monday" class="d-inline">Monday</base-checkbox>
              <base-checkbox v-model="template.available_tuesday" class="d-inline ml-3">Tuesday</base-checkbox>
              <base-checkbox v-model="template.available_wednesday" class="d-inline ml-3">Wednesday</base-checkbox>
              <base-checkbox v-model="template.available_thursday" class="d-inline ml-3">Thursday</base-checkbox>
              <base-checkbox v-model="template.available_friday" class="d-inline ml-3">Friday</base-checkbox>
              <base-checkbox v-model="template.available_saturday" class="d-inline ml-3">Saturday</base-checkbox>
              <base-checkbox v-model="template.available_sunday" class="d-inline ml-3">Sunday</base-checkbox>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-lg-3">
              <base-input label="Exclusion dates">
                <el-date-picker
                  type="dates"
                  placeholder="mm-dd-yyyy"
                  value-format="yyyy-MM-dd"
                  name="date"
                  :format="date_format_cal"
                  v-model="template.event_exclusions"
                >
                </el-date-picker>
              </base-input>
            </div>
          </div>
        </div>
      </div>
      </form-section>

      <form-section title="Eligibility rules">
      <div class="row">
        <div class="col">
          <promo-rules-manager
            :rules="template.rules"
            :available-rule-types="availableRuleTypes"
            :available-games="purchasable_items.filter(i => i.item_type === 'game')"
            :available-items="purchasable_items.filter(i => i.item_type === 'item')"
            :available-gift-cards="purchasable_items.filter(i => i.item_type === 'gift_card_type')"
            :value-type="template.value_type && template.value_type.id ? template.value_type.id : 0"
            :currency-symbol="currency_symbol"
            @add-rule="template.rules.push($event)"
            @update-rule="(index, rule) => $set(template.rules, index, rule)"
            @remove-rule="template.rules.splice($event, 1)"
          />
        </div>
      </div>
      </form-section>
      </div>

      <div class="row mt-2" v-if="add_template">
        <div class="col">
          <base-button v-on:click="cancelTemplateForm()" type="default">
            Cancel
          </base-button>
          <base-button class="ml-2" v-on:click="saveTemplate()" type="primary">
            Create promo code group
          </base-button>
        </div>
      </div>
      <div class="row mt-2" v-if="edit_template">
        <div class="col">
          <base-button v-on:click="cancelTemplateForm()" type="default">
            Cancel
          </base-button>
          <base-button class="ml-2" v-on:click="updateTemplate()" type="primary">
            Update promo code group
          </base-button>
        </div>
      </div>
    </div>

    <!-- Add Promo Codes to Group Modal -->
    <modal :show.sync="modals.add_promos_to_group"
           body-classes="p-0"
           modal-classes="modal-dialog-centered modal-lg">
      <card type="secondary"
            header-classes="bg-white pb-5"
            body-classes="px-lg-5 py-lg-5"
            class="border-0 mb-0">

        <h4>Add promo codes to <span class="text-primary">{{selectedGroup ? selectedGroup.name : ''}}</span></h4>

        <div class="row mb-4">
          <div class="col">
            <base-input label="Promo codes">
              <el-select multiple
                         filterable
                         remote
                         reserve-keyword
                         :remote-method="searchPromoCodes"
                         :loading="searchingPromos"
                         v-model="selectedPromoCodes"
                         @change="handlePromoCodeSelection"
                         ref="promoCodeSelect"
                         class="select-primary promo-code-search"
                         placeholder="Type to search promo codes..."
                         value-key="id"
              >
                <el-option
                  v-for="promo in availablePromoCodes"
                  class="select-primary"
                  :label="promo.name"
                  :value="promo"
                  :key="promo.id"
                ></el-option>
              </el-select>
            </base-input>
          </div>
        </div>

        <div class="row mb-3" v-if="selectedGroup && selectedGroup.has_template">
          <div class="col">
            <base-alert type="info" class="mb-3">
              <i class="tim-icons icon-alert-circle-exc mr-2"></i>
              <small>This group has default settings configured. Enable the toggle below to apply these settings to the selected promo codes. This will overwrite any existing settings.</small>
            </base-alert>
            <div class="d-flex align-items-center">
              <el-switch active-color="#e14eca" v-model="applyTemplateSettings" />
              <label class="mb-0 ml-2">Apply default settings to selected promo codes</label>
            </div>
          </div>
        </div>

        <div class="row text-center">
          <div class="col">
            <base-button type="default" @click="modals.add_promos_to_group = false">Cancel</base-button>
            <base-button type="primary" class="ml-2" @click="addPromoCodesToGroup()" :disabled="applyingSettings">
              <span v-if="applyingSettings"><i class="fas fa-spinner fa-spin mr-1"></i> Applying...</span>
              <span v-else>Add to group</span>
            </base-button>
          </div>
        </div>

      </card>
    </modal>

    <!-- Apply Settings to a Single Promo Code Modal -->
    <modal :show.sync="modals.view_group_promos"
           body-classes="p-0"
           modal-classes="modal-dialog-centered modal-lg">
      <card type="secondary"
            header-classes="bg-white pb-5"
            body-classes="px-lg-5 py-lg-5"
            class="border-0 mb-0">

        <h4>Apply settings in <span class="text-primary">{{selectedGroup ? selectedGroup.name : ''}}</span></h4>

        <base-alert type="info" class="mb-3">
          <i class="tim-icons icon-alert-circle-exc mr-2"></i>
          <small>Apply this group's default settings to a single promo code. This overwrites that code's existing settings (allowed days, dates, times, exclusions, applicable items, eligibility rules).</small>
        </base-alert>

        <div v-if="loadingGroupPromos" class="text-center py-4">
          <i class="el-icon-loading" style="font-size: 22px; color: #e14eca"></i>
        </div>

        <div v-else-if="groupPromos.length === 0" class="text-center py-4">
          <small class="text-muted">This group doesn't have any promo codes yet.</small>
        </div>

        <div v-else class="group-promo-list">
          <div v-for="promo in groupPromos" :key="promo.id"
               class="d-flex align-items-center justify-content-between py-2 group-promo-row">
            <span class="group-promo-name mr-3">{{ promo.name }}</span>
            <base-button type="primary" size="sm" class="mb-0"
                         :disabled="applyingPromoId !== null"
                         @click="applySettingsToOnePromo(promo)">
              <span v-if="applyingPromoId === promo.id"><i class="fas fa-spinner fa-spin mr-1"></i> Applying...</span>
              <span v-else>Apply settings</span>
            </base-button>
          </div>
        </div>

        <div class="row text-center mt-4">
          <div class="col">
            <base-button type="default" @click="modals.view_group_promos = false">Close</base-button>
          </div>
        </div>

      </card>
    </modal>

    <base-alert-dialog
      :show="confirmationDialog.show"
      :title="confirmationDialog.title"
      :message="confirmationDialog.message"
      :variant="confirmationDialog.variant"
      :show-cancel="confirmationDialog.showCancel"
      :confirm-text="confirmationDialog.confirmText"
      :confirm-variant="confirmationDialog.confirmVariant"
      @confirm="confirmationDialogConfirm"
      @cancel="confirmationDialogCancel"
    />

    <base-alert-dialog
      :show="deleteGroupDialog.show"
      title="Delete promo code group?"
      variant="danger"
      show-cancel
      confirm-text="Delete"
      confirm-variant="danger"
      @confirm="confirmDeleteGroup"
      @cancel="deleteGroupDialog.show = false">
      <div class="mb-3">{{ deleteGroupDialog.name }} has {{ deleteGroupDialog.promoCount }} promo code(s). Choose what happens to them.</div>
      <segmented-toggle v-model="deleteGroupDialog.promoCodesAction" :options="deleteGroupOptions" size="sm" />
      <info-hint
        :key="deleteGroupDialog.promoCodesAction"
        class="mt-3"
        :variant="deleteGroupDialog.promoCodesAction === 'archive' ? 'warning' : 'primary'">
        {{ selectedDeleteGroupOption.description }}
      </info-hint>
    </base-alert-dialog>
  </div>
</template>

<script>
import {API_LOCATION} from "@/api-config";
import axios from "axios";
import {Auth} from "aws-amplify";
import Badge from "@/components/Badge";
import BaseAlert from "@/components/BaseAlert";
import _ from "lodash";
import currencies from "@/assets/currencies.json";
import {Dropdown, DropdownItem, DropdownMenu, Switch, TimePicker} from "element-ui";
import {Modal, Card} from "@/components";
import PromoRulesManager from "./PromoRules/PromoRulesManager.vue";

export default {
  name: "PromoCodeTemplates",
  components: {
    Badge,
    BaseAlert,
    Modal,
    Card,
    [Switch.name]: Switch,
    [TimePicker.name]: TimePicker,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    PromoRulesManager,
  },
  data() {
    return {
      loading: false,
      confirmationDialog: {
        show: false,
        title: '',
        message: '',
        variant: 'warning',
        showCancel: false,
        confirmText: 'OK',
        confirmVariant: 'primary',
        onConfirm: null,
        onCancel: null
      },
      deleteGroupDialog: {
        show: false,
        template: null,
        name: '',
        promoCount: 0,
        promoCodesAction: 'keep'
      },
      deleteGroupOptions: [
        { value: 'keep', label: 'Keep codes', description: 'The codes stay active and show no group. Dashboards filtered by this group keep their history.' },
        { value: 'archive', label: 'Archive codes', description: 'The codes are archived with the group and can no longer be redeemed.' }
      ],
      templates: [],
      // Filter values for the <filter-bar>; search matches name or description.
      filterValues: { search: '', has_template: false, promo_count: { from: '', to: '' } },
      // Server sort; key is a get-promo-code-templates sort key. Seeded from the URL in mounted.
      sort: { key: 'name', dir: 'asc' },
      // FilterBar (saved views) drives the first fetch via its initial change; this guards it.
      didInitialFetch: false,
      isMobile: false,
      perPage: 10,
      currentPage: 1,
      totalRows: 0,
      modals: {
        add_promos_to_group: false,
        view_group_promos: false
      },
      selectedGroup: null,
      groupPromos: [],
      loadingGroupPromos: false,
      applyingPromoId: null,
      availablePromoCodes: [],
      selectedPromoCodes: [],
      searchingPromos: false,
      debouncedPromoSearch: null,
      applyTemplateSettings: false,
      applyingSettings: false,
      currencies: currencies.currencies,
      currency: JSON.parse(localStorage.getItem('group')).currency,
      currency_symbol: '$',
      availableRuleTypes: [],
      debouncedSearch: null,
      add_template: false,
      edit_template: false,
      show_template_details: false,
      un_key: 0,
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      date_format_cal: '',
      picker_time_format: 'hh:mm A',
      purchasable_items: [],
      template: {
        name: '',
        description: '',
        redemption_type: {name: 'Percentage discount', id: 0},
        value_amount: '1',
        value_type: {name: 'Per transaction', id: 0},
        purchase_all_dates: {id: 0, name: 'Available any dates'},
        purchase_start_date: new Date(),
        purchase_end_date: new Date(),
        event_all_dates: {id: 0, name: 'Available any dates'},
        event_all_times: {id: 0, name: 'Available any times'},
        available_monday: true,
        available_tuesday: true,
        available_wednesday: true,
        available_thursday: true,
        available_friday: true,
        available_saturday: true,
        available_sunday: true,
        event_exclusions: [],
        event_start_date: new Date(),
        event_end_date: new Date(),
        event_start_time: new Date(),
        event_end_time: new Date(),
        is_combineable: false,
        internal_only: false,
        purchasable_items: [],
        rules: []
      },
      errs: {
        name: '',
        value_amount: '',
        purchase_start_date: '',
        purchase_end_date: '',
        event_start_date: '',
        event_end_date: '',
        event_start_time: '',
        event_end_time: ''
      },
      redemptionTypes: [
        {name: 'Percentage discount', id: 0},
        {name: 'Value discount', id: 1}
      ],
      valueTypes: [
        {name: 'Per transaction', id: 0},
        {name: 'Per line item', id: 1},
        {name: 'Per quantity', id: 2}
      ],
      timeOptions: [
        {name: 'Available any times', id: 0},
        {name: 'Available between times', id: 1}
      ],
      dateOptions: [
        {name: 'Available any dates', id: 0},
        {name: 'Available between dates', id: 1}
      ]
    }
  },
  computed: {
    // Column keys equal the lambda sort keys so DataTable highlights the sorted header.
    groupColumns() {
      return [
        { key: 'name', label: 'Promo code group', sortable: true },
        { key: 'description', label: 'Description' },
        { key: 'promo_count', label: 'Promo codes', sortable: true }
      ]
    },
    // Saved views store the sort; `name` A to Z is the default.
    groupSortOptions() {
      return [
        { key: 'name', label: 'Promo code group' },
        { key: 'promo_count', label: 'Promo codes' },
        { key: 'created', label: 'Date created' }
      ]
    },
    groupFilterSchema() {
      return [
        { key: 'search', type: 'text', label: 'Promo code group', placeholder: 'Name or description', operatorLabel: 'contains', primary: true },
        { key: 'has_template', type: 'toggle', label: 'Has default settings', primary: true },
        { key: 'promo_count', type: 'numberrange', label: 'Promo codes', primary: true }
      ]
    },
    selectedDeleteGroupOption() {
      return this.deleteGroupOptions.find(o => o.value === this.deleteGroupDialog.promoCodesAction) || this.deleteGroupOptions[0]
    },
  },
  methods: {
    formatCurrencyValue(amount) {
      const formattedAmount = parseFloat(amount).toFixed(2);
      const currencyInfo = _.find(this.currencies, { code: this.currency });
      const isPostfix = currencyInfo ? currencyInfo.postfix : false;

      if (isPostfix) {
        return formattedAmount + this.currency_symbol;
      } else {
        return this.currency_symbol + formattedAmount;
      }
    },
    getAuthToken() {
      return new Promise(function(resolve) {
        Auth.currentSession()
          .then(data => {
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    async getTemplates(count, offset) {
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promo-code-templates?' +
        'count=' + count + '&offset=' + offset + '&' + this.groupFilterQuery()

      this.loading = true
      axios.get(url, config)
        .then((res) => {
          this.templates = res.data.templates
          this.totalRows = res.data.total_rows
          this.purchasable_items = res.data.purchasable_items
          this.availableRuleTypes = res.data.rule_types || []
          this.loading = false
          this.addQueryParameters()
        })
        .catch((err) => {
          this.loading = false
          console.error(err)
          if(err.response && err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    showAlert(title, message, variant = 'info', onConfirm = null){
      this.confirmationDialog = {
        show: true, title, message, variant,
        showCancel: false, confirmText: 'OK', confirmVariant: 'primary', onConfirm, onCancel: null
      }
    },
    // Resolves true on confirm and false on cancel, ESC or an outside click.
    confirmAction({ title, message, variant = 'warning', confirmText = 'Confirm', confirmVariant = 'primary' }){
      return new Promise(resolve => {
        this.confirmationDialog = {
          show: true, title, message, variant,
          showCancel: true, confirmText, confirmVariant,
          onConfirm: () => resolve(true), onCancel: () => resolve(false)
        }
      })
    },
    confirmationDialogConfirm(){
      const action = this.confirmationDialog.onConfirm
      this.confirmationDialog.show = false
      if(action){
        action()
      }
    },
    confirmationDialogCancel(){
      const action = this.confirmationDialog.onCancel
      this.confirmationDialog.show = false
      if(action){
        action()
      }
    },
    getOffset(currentPage, perPage) {
      return Number((currentPage * perPage) - perPage)
    },
    // Filter and sort params for the group list; names match get-promo-code-templates.
    groupFilterQuery() {
      const f = this.filterValues
      const parts = ['search=' + encodeURIComponent(f.search || '')]
      if (f.has_template === true) parts.push('has_template=1')
      const range = f.promo_count || {}
      if (range.from !== '' && range.from != null && range.to !== '' && range.to != null) {
        parts.push('promo_count_from=' + encodeURIComponent(range.from), 'promo_count_to=' + encodeURIComponent(range.to))
      }
      parts.push('sort=' + encodeURIComponent(this.sort.key), 'dir=' + this.sort.dir)
      return parts.join('&')
    },
    // FilterBar change: apply new values and (re)fetch. The first change is FilterBar's
    // initial saved-views resolution and drives the first load (preserving the URL page).
    onFiltersChanged(values) {
      this.filterValues = {
        search: values.search || '',
        has_template: values.has_template === true,
        promo_count: values.promo_count || { from: '', to: '' }
      }
      if (!this.didInitialFetch) {
        this.didInitialFetch = true
      } else {
        this.currentPage = 1
      }
      this.getTemplates(this.perPage, this.getOffset(this.currentPage, this.perPage))
    },
    // Header click or a restored saved view. Before the first fetch FilterBar is
    // restoring the view, and its initial change does the fetch.
    onSortChange(value) {
      this.sort = { key: value.key, dir: value.dir === 'desc' ? 'desc' : 'asc' }
      if (this.didInitialFetch) {
        this.currentPage = 1
        this.getTemplates(this.perPage, this.getOffset(this.currentPage, this.perPage))
      }
    },
    onPageChange(page) {
      this.currentPage = page
      this.getTemplates(this.perPage, this.getOffset(this.currentPage, this.perPage))
    },
    onPerPageChange(n) {
      this.perPage = n
      this.currentPage = 1
      this.getTemplates(this.perPage, this.getOffset(this.currentPage, this.perPage))
    },
    addQueryParameters() {
      const newQuery = {
        ...this.$route.query,
        count: this.perPage,
        page: this.currentPage,
        search: this.filterValues.search,
        // Own keys: the promo codes tab writes sort and dir to the same URL.
        group_sort: this.sort.key,
        group_dir: this.sort.dir
      }
      this.$router.replace({query: newQuery}).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err;
        }
      });
    },
    createNewTemplate() {
      this.template = {
        name: '',
        description: '',
        redemption_type: {name: 'Percentage discount', id: 0},
        value_amount: '1',
        value_type: {name: 'Per transaction', id: 0},
        purchase_all_dates: {id: 0, name: 'Available any dates'},
        purchase_start_date: new Date(),
        purchase_end_date: new Date(),
        event_all_dates: {id: 0, name: 'Available any dates'},
        event_all_times: {id: 0, name: 'Available any times'},
        available_monday: true,
        available_tuesday: true,
        available_wednesday: true,
        available_thursday: true,
        available_friday: true,
        available_saturday: true,
        available_sunday: true,
        event_exclusions: [],
        event_start_date: new Date(),
        event_end_date: new Date(),
        event_start_time: new Date(),
        event_end_time: new Date(),
        is_combineable: false,
        internal_only: false,
        purchasable_items: [],
        rules: []
      }
      this.show_template_details = false
      this.add_template = true
    },
    editTemplate(item, event) {
      event.preventDefault()
      this.template = JSON.parse(JSON.stringify(item))
      this.template.redemption_type = {id: item.redemption_type}
      this.template.value_type = {id: item.value_type}
      this.template.purchase_all_dates = {id: item.purchase_is_range ? 1 : 0}
      this.template.event_all_dates = {id: item.event_is_date_range ? 1 : 0}
      this.template.event_all_times = {id: item.event_is_time_range ? 1 : 0}

      let dt = new Date()
      if(item.event_start_time && !(item.event_start_time instanceof Date)){
        dt.setHours(item.event_start_time.split(':')[0])
        dt.setMinutes(item.event_start_time.split(':')[1])
        this.template.event_start_time = dt
      }
      if(item.event_end_time && !(item.event_end_time instanceof Date)){
        dt = new Date()
        dt.setHours(item.event_end_time.split(':')[0])
        dt.setMinutes(item.event_end_time.split(':')[1])
        this.template.event_end_time = dt
      }

      this.template.rules = Array.isArray(item.rules) ? JSON.parse(JSON.stringify(item.rules)) : []
      this.show_template_details = item.has_template ? true : false
      this.edit_template = true
    },
    cancelTemplateForm() {
      this.add_template = false
      this.edit_template = false
      this.show_template_details = false
      this.getTemplates(this.perPage, this.getOffset(this.currentPage, this.perPage))
    },
    isTemplateValid() {
      let errors = 0

      if(this.template.name.length <= 0){
        this.errs.name = 'Group name cannot be empty'
        errors++
      } else {
        this.errs.name = ''
      }

      if(isNaN(this.template.value_amount) || parseFloat(this.template.value_amount) < 0){
        this.errs.value_amount = 'Value amount must be greater or equal to 0'
        errors++
      } else {
        this.errs.value_amount = ''
      }

      if(errors > 0){
        return false
      }
      return true
    },
    validateDateRange(){
      let errors = 0
      if(this.template.purchase_all_dates.id === 1){
        if(this.template.purchase_start_date instanceof Date){
          this.template.purchase_start_date = this.template.purchase_start_date.toISOString()
        }
        if(this.template.purchase_end_date instanceof Date){
          this.template.purchase_end_date = this.template.purchase_end_date.toISOString()
        }
        if(this.template.purchase_start_date.includes('T')){
          this.template.purchase_start_date = this.template.purchase_start_date.split('T')[0]
        }
        if(this.template.purchase_end_date.includes('T')){
          this.template.purchase_end_date = this.template.purchase_end_date.split('T')[0]
        }
        let start = new Date(this.template.purchase_start_date)
        let end = new Date(this.template.purchase_end_date)
        if(start > end){
          this.errs.purchase_start_date = 'Start date cannot be greater than end date'
          errors++
        } else {
          this.errs.purchase_start_date = ''
        }
      }

      if(this.template.event_all_dates.id === 1){
        if(this.template.event_start_date instanceof Date){
          this.template.event_start_date = this.template.event_start_date.toISOString()
        }
        if(this.template.event_end_date instanceof Date){
          this.template.event_end_date = this.template.event_end_date.toISOString()
        }
        if(this.template.event_start_date.includes('T')){
          this.template.event_start_date = this.template.event_start_date.split('T')[0]
        }
        if(this.template.event_end_date.includes('T')){
          this.template.event_end_date = this.template.event_end_date.split('T')[0]
        }
        let event_start = new Date(this.template.event_start_date)
        let event_end = new Date(this.template.event_end_date)
        if(event_start > event_end){
          this.errs.event_start_date = 'Start date cannot be greater than end date'
          errors++
        } else {
          this.errs.event_start_date = ''
        }
      }

      if(this.template.event_all_times.id === 1){
        // Compare time of day only: typed picker input parses to Jan 1 while
        // panel-picked values keep today's date, so full Dates aren't comparable
        const startMinutes = this.template.event_start_time.getHours() * 60 + this.template.event_start_time.getMinutes()
        const endMinutes = this.template.event_end_time.getHours() * 60 + this.template.event_end_time.getMinutes()
        if(startMinutes > endMinutes){
          this.errs.event_start_time = 'Start time cannot be greater than end time'
          errors++
        } else {
          this.errs.event_start_time = ''
        }
      }

      return errors
    },
    async saveTemplate() {
      if(!this.isTemplateValid() || this.validateDateRange() > 0){
        return
      }

      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promo-code-templates'

      let data = JSON.parse(JSON.stringify(this.template))
      data.has_template = this.show_template_details ? 1 : 0
      data.rules = this.show_template_details ? (this.template.rules || []) : []
      if(data.event_start_time instanceof Date) {
        data.event_start_time = data.event_start_time.getHours() + ':' + data.event_start_time.getMinutes() + ':00'
      }
      if(data.event_end_time instanceof Date) {
        data.event_end_time = data.event_end_time.getHours() + ':' + data.event_end_time.getMinutes() + ':00'
      }

      axios.post(url, data, config)
        .then(response => {
          this.showAlert('Success', 'Promo code group has been created', 'success')
          this.add_template = false
          this.show_template_details = false
          this.getTemplates(this.perPage, this.getOffset(this.currentPage, this.perPage))
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response && err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    async updateTemplate() {
      if(!this.isTemplateValid() || this.validateDateRange() > 0){
        return
      }

      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promo-code-templates/' + this.template.id

      let data = JSON.parse(JSON.stringify(this.template))
      data.has_template = this.show_template_details ? 1 : 0
      data.rules = this.show_template_details ? (this.template.rules || []) : []
      if(data.event_start_time instanceof Date) {
        data.event_start_time = data.event_start_time.getHours() + ':' + data.event_start_time.getMinutes() + ':00'
      }
      if(data.event_end_time instanceof Date) {
        data.event_end_time = data.event_end_time.getHours() + ':' + data.event_end_time.getMinutes() + ':00'
      }

      axios.put(url, data, config)
        .then(response => {
          this.showAlert('Success', 'Promo code group has been updated', 'success')
          this.edit_template = false
          this.show_template_details = false
          this.getTemplates(this.perPage, this.getOffset(this.currentPage, this.perPage))
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response && err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    createPromoFromTemplate(template, event) {
      event.preventDefault()
      this.$emit('create-from-template', template)
    },
    async addPromoToGroup(group, event) {
      event.preventDefault()
      this.selectedGroup = group
      this.selectedPromoCodes = []
      this.availablePromoCodes = []
      this.applyTemplateSettings = false
      this.modals.add_promos_to_group = true
    },
    searchPromoCodes(query) {
      if (this.debouncedPromoSearch) {
        this.debouncedPromoSearch(query)
      }
    },
    handlePromoCodeSelection() {
      // Clear the search results and query to reset the input
      this.$nextTick(() => {
        this.availablePromoCodes = []
        if (this.$refs.promoCodeSelect) {
          this.$refs.promoCodeSelect.query = ''
        }
      })
    },
    async performPromoSearch(query) {
      if (query !== '') {
        this.searchingPromos = true
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let companyId = localStorage.getItem('companyId')
        let companyGroupId = JSON.parse(localStorage.getItem('group')).id

        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promos?autocomplete=' + encodeURIComponent(query)

        axios.get(url, config)
          .then((res) => {
            this.availablePromoCodes = res.data.promos || []
            this.searchingPromos = false
          })
          .catch((err) => {
            this.searchingPromos = false
            console.error(err)
            if(err.response && err.response.status == 401){
              this.$router.push('/forbidden')
            } else {
              this.showAlert('Error', 'Failed to search promo codes', 'danger')
            }
          })
      } else {
        this.availablePromoCodes = []
      }
    },
    async addPromoCodesToGroup() {
      if(this.selectedPromoCodes.length === 0) {
        this.showAlert('Error', 'Please select at least one promo code', 'danger')
        return
      }

      this.loading = true
      this.applyingSettings = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promo-code-templates/' + this.selectedGroup.id + '/add-promos'

      let data = {
        promo_code_ids: this.selectedPromoCodes.map(p => p.id)
      }

      try {
        await axios.post(url, data, config)

        // If user checked "Apply default settings", call bulk update endpoint
        if (this.applyTemplateSettings && this.selectedGroup.has_template) {
          await this.applyTemplateSettingsToPromoCodes(this.selectedPromoCodes.map(p => p.id))
        }

        this.showAlert('Success', `${this.selectedPromoCodes.length} promo code(s) added to ${this.selectedGroup.name}${this.applyTemplateSettings ? ' with default settings applied' : ''}`, 'success')
        this.modals.add_promos_to_group = false
        this.selectedPromoCodes = []
        this.applyTemplateSettings = false
        this.getTemplates(this.perPage, this.getOffset(this.currentPage, this.perPage))
      } catch(err) {
        console.log(err)
        if(err.response && err.response.status == 401){
          this.$router.push('/forbidden')
        } else {
          this.showAlert('Error', 'Failed to add promo codes to group', 'danger')
        }
      } finally {
        this.loading = false
        this.applyingSettings = false
      }
    },
    getValueTypeName(id) {
      const types = ['Per transaction', 'Per line item', 'Per quantity']
      return types[id] || 'Unknown'
    },
    mapTemplateToSettings(template) {
      return {
        redemption_type: { id: template.redemption_type },
        value_type: { id: template.value_type },
        value_amount: template.value_amount,
        purchase_all_dates: { id: template.purchase_is_range ? 1 : 0 },
        purchase_start_date: template.purchase_start_date,
        purchase_end_date: template.purchase_end_date,
        event_all_dates: { id: template.event_is_date_range ? 1 : 0 },
        event_start_date: template.event_start_date,
        event_end_date: template.event_end_date,
        event_all_times: { id: template.event_is_time_range ? 1 : 0 },
        event_start_time: template.event_start_time,
        event_end_time: template.event_end_time,
        available_monday: template.available_monday,
        available_tuesday: template.available_tuesday,
        available_wednesday: template.available_wednesday,
        available_thursday: template.available_thursday,
        available_friday: template.available_friday,
        available_saturday: template.available_saturday,
        available_sunday: template.available_sunday,
        is_combineable: template.is_combineable,
        internal_only: template.internal_only,
        purchasable_items: template.purchasable_items || [],
        event_exlusions: template.event_exclusions || [],
        rules: template.rules || []
      }
    },
    getTemplateFields(template) {
      // Return all fields that the bulk update endpoint expects
      // Note: Description is intentionally excluded - group description is for the group, not promo codes
      let fields = [
        { id: 1, name: 'Redemption type' },
        { id: 2, name: 'Value type' },
        { id: 3, name: 'Amount' },
        { id: 5, name: 'Allowed purchase dates' },
        { id: 8, name: 'Can be combined with other promo codes' },
        { id: 9, name: 'Can only be applied internally' },
        { id: 10, name: 'Applicable to' },
        { id: 11, name: 'Allowed booking dates' },
        { id: 14, name: 'Allowed booking time' },
        { id: 17, name: 'Allowed days of the week' },
        { id: 18, name: 'Exclusion dates' },
        { id: 19, name: 'Eligibility rules' }
      ]

      // Add date range fields if applicable
      if (template.purchase_is_range) {
        fields.push({ id: 6, name: 'Purchase start date' })
        fields.push({ id: 7, name: 'Purchase end date' })
      }
      if (template.event_is_date_range) {
        fields.push({ id: 12, name: 'Event start date' })
        fields.push({ id: 13, name: 'Event end date' })
      }
      if (template.event_is_time_range) {
        fields.push({ id: 15, name: 'Event start time' })
        fields.push({ id: 16, name: 'Event end time' })
      }

      return fields
    },
    async applyTemplateSettingsToPromoCodes(promoCodeIds) {
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promos'

      const settings = this.mapTemplateToSettings(this.selectedGroup)
      const fields = this.getTemplateFields(this.selectedGroup)

      // Extract games from purchasable_items
      const games = (this.selectedGroup.purchasable_items || [])
        .filter(item => item.item_type === 'game')
        .map(item => ({ id: item.id }))

      let data = {
        promo_codes: promoCodeIds,
        settings: settings,
        fields: fields,
        games: games,
        purchasable_items: this.selectedGroup.purchasable_items || []
      }

      await axios.put(url, data, config)
    },
    async applySettingsToAllPromoCodesPrompt(group, event) {
      event.preventDefault()

      this.loading = true
      let promoIds = []
      try {
        promoIds = await this.fetchPromoIdsForTemplate(group.id)
      } catch(err) {
        this.loading = false
        console.error(err)
        if(err.response && err.response.status == 401){
          this.$router.push('/forbidden')
          return
        }
        this.showAlert('Error', 'Failed to load promo codes for this group', 'danger')
        return
      }
      this.loading = false

      if(promoIds.length === 0) {
        this.showAlert('No promo codes', `${group.name} doesn't have any promo codes to apply settings to.`, 'info')
        return
      }

      const confirmed = await this.confirmAction({
        title: 'Apply settings?',
        message: `This will overwrite settings on ${promoIds.length} promo code(s) in ${group.name}. ` +
          'Existing settings (allowed days, dates, times, exclusions, applicable items, etc.) will be replaced with the group defaults.',
        confirmText: 'Apply settings'
      })

      if(!confirmed) return

      this.loading = true
      this.selectedGroup = group
      try {
        await this.applyTemplateSettingsToPromoCodes(promoIds)
        this.showAlert('Success', `Applied settings to ${promoIds.length} promo code(s) in ${group.name}`, 'success')
        this.getTemplates(this.perPage, this.getOffset(this.currentPage, this.perPage))
      } catch(err) {
        console.error(err)
        if(err.response && err.response.status == 401){
          this.$router.push('/forbidden')
        } else {
          this.showAlert('Error', 'Failed to apply settings to promo codes', 'danger')
        }
      } finally {
        this.loading = false
        this.selectedGroup = null
      }
    },
    async fetchPromoIdsForTemplate(templateId) {
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promos?minimal=1'

      const res = await axios.get(url, config)
      const promos = res.data.promos || []
      return promos
        .filter(p => p.template_id === templateId)
        .map(p => p.id)
    },
    async fetchPromosForTemplate(templateId) {
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promos?minimal=1'

      const res = await axios.get(url, config)
      const promos = res.data.promos || []
      return promos
        .filter(p => p.template_id === templateId)
        .map(p => ({ id: p.id, name: p.name }))
    },
    async viewGroupPromos(group, event) {
      if (event) event.preventDefault()
      this.selectedGroup = group
      this.groupPromos = []
      this.applyingPromoId = null
      this.modals.view_group_promos = true
      this.loadingGroupPromos = true
      try {
        this.groupPromos = await this.fetchPromosForTemplate(group.id)
      } catch (err) {
        console.error(err)
        if (err.response && err.response.status == 401) {
          this.$router.push('/forbidden')
        } else {
          this.showAlert('Error', 'Failed to load promo codes for this group', 'danger')
        }
      } finally {
        this.loadingGroupPromos = false
      }
    },
    async applySettingsToOnePromo(promo) {
      const confirmed = await this.confirmAction({
        title: 'Apply group settings?',
        message: `This will overwrite the settings on ${promo.name} with the default settings from ${this.selectedGroup.name}. ` +
          'Existing settings (allowed days, dates, times, exclusions, applicable items) will be replaced.',
        confirmText: 'Apply settings'
      })

      if (!confirmed) return

      this.applyingPromoId = promo.id
      try {
        await this.applyTemplateSettingsToPromoCodes([promo.id])
        this.showAlert('Success', `Applied ${this.selectedGroup.name} default settings to ${promo.name}`, 'success')
      } catch (err) {
        console.error(err)
        if (err.response && err.response.status == 401) {
          this.$router.push('/forbidden')
        } else {
          this.showAlert('Error', 'Failed to apply settings to promo code', 'danger')
        }
      } finally {
        this.applyingPromoId = null
      }
    },
    async deleteTemplatePrompt(template, event) {
      event.preventDefault()
      // A group with promo codes asks what happens to them; an empty group is a plain confirm.
      if(Number(template.promo_count) > 0){
        this.deleteGroupDialog = {
          show: true,
          template,
          name: template.name,
          promoCount: Number(template.promo_count),
          promoCodesAction: 'keep'
        }
        return
      }
      const confirmed = await this.confirmAction({
        title: 'Are you sure?',
        message: 'This will permanently delete this promo code group.',
        variant: 'danger',
        confirmVariant: 'danger',
        confirmText: 'Delete'
      })
      if(confirmed){
        this.deleteTemplate(template, 'keep')
      }
    },
    confirmDeleteGroup() {
      const { template, promoCodesAction } = this.deleteGroupDialog
      this.deleteGroupDialog.show = false
      this.deleteTemplate(template, promoCodesAction)
    },
    async deleteTemplate(template, promoCodesAction) {
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/promo-code-templates/' + template.id +
        '?promo_codes=' + encodeURIComponent(promoCodesAction)

      try {
        await axios.delete(url, config)
        this.showAlert('Success', 'Promo code group has been deleted', 'success')
        this.getTemplates(this.perPage, this.getOffset(this.currentPage, this.perPage))
      } catch(err) {
        console.log(err)
        if(err.response && err.response.status == 401){
          this.$router.push('/forbidden')
        } else {
          this.showAlert('Error', 'Something went wrong', 'danger')
        }
      } finally {
        this.loading = false
      }
    },
    addAllPurchaseTypes(collection, type) {
      const existingIds = new Set(this.template.purchasable_items.map(item => item.id));
      const newItems = collection.filter(item => item.item_type === type && !existingIds.has(item.id));
      this.template.purchasable_items = this.template.purchasable_items.concat(newItems);
      this.un_key++
    },
    appliesToGames(){
      if(!this.template.purchasable_items){
        return false
      }
      return this.template.purchasable_items.some(item => item.item_type === 'game');
    }
  },
  created() {
    // DataTable hides its headers at 767px, so phones sort from the filter bar chip.
    this._mobileMql = window.matchMedia('(max-width: 767px)')
    this._onMobileMql = () => { this.isMobile = this._mobileMql.matches }
    this._onMobileMql()
    this._mobileMql.addEventListener('change', this._onMobileMql)
  },
  beforeDestroy() {
    this._mobileMql.removeEventListener('change', this._onMobileMql)
  },
  mounted() {
    this.currency_symbol = _.find(this.currencies, { code: JSON.parse(localStorage.getItem('group')).currency }).symbol
    this.perPage = Number(this.$route.query.count) || 10
    this.currentPage = Number(this.$route.query.page) || 1
    // Seed FilterBar from the URL; its initial change drives the first fetch.
    this.filterValues = { ...this.filterValues, search: this.$route.query.search || '' }
    const urlSort = this.$route.query.group_sort
    if (urlSort && this.groupSortOptions.some(o => o.key === urlSort)) {
      this.sort = { key: urlSort, dir: this.$route.query.group_dir === 'desc' ? 'desc' : 'asc' }
    }

    this.debouncedSearch = _.debounce(() => {
      this.currentPage = 1
      this.getTemplates(this.perPage, this.getOffset(this.currentPage, this.perPage))
    }, 500)

    this.debouncedPromoSearch = _.debounce((query) => {
      this.performPromoSearch(query)
    }, 300)
  }
}
</script>

<style scoped>
/* Promo code search input styling */
.promo-code-search ::v-deep .el-input__inner {
  color: white !important;
  background-color: transparent !important;
  border-color: #e14eca !important;
}

.promo-code-search ::v-deep .el-input__inner:focus {
  border-color: #e14eca !important;
}

.promo-code-search ::v-deep .el-select .el-input.is-focus .el-input__inner {
  border-color: #e14eca !important;
}

.promo-code-search ::v-deep .el-tag {
  color: white !important;
  background-color: #e14eca !important;
  border-color: #e14eca !important;
}

.promo-code-search ::v-deep .el-tag__close {
  color: white !important;
}

.promo-code-search ::v-deep .el-select__input {
  color: white !important;
}

.promo-code-search ::v-deep .el-tag.el-tag--info {
  background-color: #e14eca !important;
  border-color: #e14eca !important;
}

.group-promo-list {
  max-height: 360px;
  overflow-y: auto;
}
.group-promo-row {
  border-bottom: 1px solid rgba(125, 125, 125, 0.2);
}
.group-promo-row:last-child {
  border-bottom: none;
}
.group-promo-name {
  font-weight: 500;
  word-break: break-word;
}

/* Disabled-but-visible dropdown action (e.g. apply settings when the group has no defaults) */
.dropdown-item-na {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>

<style>
.recommended-badge {
  font-size: 10px;
  font-weight: 500;
  color: #e14eca;
  background: rgba(225, 78, 202, 0.12);
  border-radius: 3px;
  padding: 1px 6px;
  margin-left: 8px;
  letter-spacing: 0.3px;
}
</style>
